import React, { useEffect } from "react";
import styled from "@emotion/styled";
import BasicButton from "@components/button/BasicButton";
import { useNavigate, useSearchParams } from "react-router-dom";
import Image from "@components/item/Image";
import { useTheme } from "@emotion/react";
import { useApiRequest } from "@utils/ApiRequest";
import { useMutation } from "@tanstack/react-query";
import useOrderStore from "@store/order/useOrderStore";
import { Flex, Typography } from "antd";
import useOrderIdStore from "@store/order/useOrderIdStore";
import { queryClient } from "@libs/reactQuery/queryClient";
import { useTranslation } from "react-i18next";
const { Paragraph } = Typography;

const Container = styled.div(({ theme }) => ({
  padding: "2rem",
  backgroundColor: theme.color.white,
  // minHeight: "calc(100vh - 5.6rem)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "100dvh",
  position: "relative",
  gap: "4rem",
}));

const ContentWrapper = styled.div({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "2rem",

  // marginTop: "28.2rem",
});

const CheckIcon = styled.div({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "4rem",
});

const Title = styled.h1(({ theme }) => ({
  fontSize: "2.4rem",
  fontWeight: theme.fontWeight.medium,
  textAlign: "center",
}));

const MessageBox = styled.div({
  width: "100%",

  // position: "absolute",
  // bottom: "14rem",
  // padding: "0 2rem",
  marginBottom: "6rem",
});

const Message = styled.h2(({ theme }) => ({
  fontSize: "1.6rem",
  fontWeight: theme.fontWeight.medium,
  marginBottom: "2rem",
}));

const MessageList = styled.ul({
  listStyle: "none",
  padding: 0,
  margin: 0,
});

const MessageItem = styled.li(({ theme }) => ({
  fontSize: "1.6rem",
  color: theme.color.gray,
  lineHeight: "2.4rem",
  marginBottom: "1.2rem",
  position: "relative",
  paddingLeft: "1.2rem",

  "&:before": {
    content: '"•"',
    position: "absolute",
    left: 0,
  },

  "&:last-child": {
    marginBottom: 0,
  },
}));

const AccountBox = styled.div(({ theme }) => ({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  padding: "1rem",
  gap: "1rem",
  fontSize: "1.4rem",
  backgroundColor: theme.color.light_green,
  // margin: "4.6rem 0",

  "& > span": {
    fontSize: "1.2rem",
    lineHeight: "1.8rem",
  },

  "& > div": {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
  },
}));

const ButtonWrapper = styled.div({
  position: "absolute",
  bottom: "4.8rem",
  width: "100%",
  padding: "0 2rem",
});

const Bold = styled.div(({ theme }) => ({
  fontWeight: theme.fontWeight.bold,
  fontSize: "1.4rem",
  lineHeight: "2.1rem",
}));

const OrderSuccess = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { postPaymentConfirm, deleteCart } = useApiRequest();
  const [searchParams] = useSearchParams();
  const { clearOrder, cartIds } = useOrderStore();
  const { clearOrderId } = useOrderIdStore();
  const method = searchParams.get("method");
  const { t } = useTranslation();

  // 장바구니 아이템 삭제
  const { mutate: deleteMutate } = useMutation({
    mutationFn: deleteCart,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["cartList"] }),
  });

  // 결제 승인 요청
  const { mutate: mutatePaymentConfirm } = useMutation({
    mutationFn: ({
      id,
      paymentKey,
      amount,
      method,
    }: {
      id: number;
      paymentKey: any;
      amount: any;
      method: string;
    }) => postPaymentConfirm(id, amount, paymentKey, method),
    onSuccess: () => {
      navigate("/success");
      clearOrder(); // 주문 데이터 삭제
      clearOrderId(); // 주문ID 삭제
      deleteMutate(cartIds); // 결제 승인 성공 시 장바구니 아이템 삭제
    },
    onError: () => {
      navigate("/fail");
    },
  });

  useEffect(() => {
    if (method === "C" || method === "P") {
      const paymentKey = searchParams.get("paymentKey");
      const amount = searchParams.get("amount");
      const orderIdString = searchParams.get("orderId");

      if (orderIdString) {
        const order_id = orderIdString?.replace("ORDER_", "");

        // 결제 승인 요청
        mutatePaymentConfirm({
          id: Number(order_id),
          amount,
          paymentKey,
          method,
        });
      }
    }
  }, [searchParams]);

  return (
    <Container>
      <CheckIcon>
        <Image
          src={theme.image.check_circle.default}
          width="6rem"
          height="6rem"
        />
        <Title>{t("주문이 완료되었습니다")}</Title>
      </CheckIcon>

      <ContentWrapper>
        {/* 무통장입금 결제일 때 */}
        {method === "B" && (
          <AccountBox>
            <span>{t("무통장입금 결제 계좌번호")}</span>
            <Bold>{t("(주)아울소싸이어티")}</Bold>

            <div>
              <Paragraph
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "0rem",
                }}
                copyable={{
                  text: "100-036-320333",
                  icon: [
                    <Image
                      src={theme.image.copy_icon}
                      width="1.6rem"
                      height="1.6rem"
                    />,
                  ],
                }}>
                신한은행 100-036-320333
              </Paragraph>
            </div>
          </AccountBox>
        )}

        <MessageBox>
          <Message>{t("확인해주세요!")}</Message>
          <MessageList>
            <MessageItem>
              {t(
                "결제하신 금액은 구매확정 시점까지 사카라카에서 안전하게 보관해드립니다.",
              )}
            </MessageItem>
            <MessageItem>{t("상품이 발송되면 알림을 보내드려요.")}</MessageItem>
            <MessageItem>
              {t(
                "원활한 정산 진행을 위해 상품 수령 후 주문내역에서 구매 확정을 꼬옥 눌러주세요",
              )}
            </MessageItem>
          </MessageList>
        </MessageBox>

        <ButtonWrapper>
          <BasicButton
            title="주문내역 확인하기"
            width="100%"
            onClick={() => navigate("/order-list")}
            height="5.2rem"
          />
        </ButtonWrapper>
      </ContentWrapper>
    </Container>
  );
};

export default OrderSuccess;
